"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { type SubmitEventHandler, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useTransitionRouter } from "next-view-transitions";
import posthog from "posthog-js";
import type { HttpError } from "@vita-learning/types";
import { PrimaryButton as Button } from "~/client/components/buttons/PrimaryButton";
import { Link } from "~/client/components/interactives/Link";
import { TextFormGroup, useTextFormGroup } from "~/client/components/formgroups/TextFormGroup";
import { usePopupQueue } from "~/client/components/portals/Popup";
import { makeHandleHttpError, makePosthogEventName as ph, sendResetPasswordToken } from "~/shared/services";
import { isValidEmail, sanitizeArrayQueryParam } from "~/shared/utils";
export const Form: FC = () => {
  const $ = _c(24);
  const router = useTransitionRouter();
  const searchParams = useSearchParams();
  const {
    enqueueSuccess,
    enqueueError
  } = usePopupQueue();
  const [serverError, setServerError] = useState(null);
  let t0;
  if ($[0] !== searchParams) {
    t0 = sanitizeArrayQueryParam(searchParams?.get("email") ?? undefined);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== serverError || $[3] !== t0) {
    t1 = {
      initialValue: t0,
      label: "Email",
      "data-testid": "user-email-error",
      serverError,
      type: "email",
      validate: _temp
    };
    $[2] = serverError;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const email = useTextFormGroup(t1);
  let t2;
  if ($[5] !== email) {
    t2 = {
      email
    };
    $[5] = email;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const fields = t2;
  let t3;
  if ($[7] !== email.value) {
    t3 = isValidEmail(email.value);
    $[7] = email.value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const isEnabled = t3;
  let t4;
  if ($[9] !== email.value || $[10] !== enqueueError || $[11] !== enqueueSuccess || $[12] !== isEnabled || $[13] !== router) {
    t4 = async e => {
      e.preventDefault();
      ;
      try {
        if (!isEnabled) {
          return;
        }
        await sendResetPasswordToken({
          email: email.value
        });
        enqueueSuccess(`A reset link has been sent to '${email.value}'`, "Check your Inbox");
        posthog.capture(ph("login_flow", "password_reset", "requested"), {
          email: email.value
        });
        setTimeout(() => router.push("/login"), 3000);
      } catch (t5) {
        const e_0 = t5;
        const handleHttpError = makeHandleHttpError("RESET_PASSWORD_ERROR");
        const error = handleHttpError(e_0);
        setServerError(error);
        if (!error.id || error.id !== "USER_EMAIL") {
          enqueueError(error.message);
        }
      }
    };
    $[9] = email.value;
    $[10] = enqueueError;
    $[11] = enqueueSuccess;
    $[12] = isEnabled;
    $[13] = router;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleSubmit = t4;
  let t5;
  if ($[15] !== fields.email) {
    t5 = <TextFormGroup {...fields.email} />;
    $[15] = fields.email;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = !isEnabled;
  let t7;
  if ($[17] !== t6) {
    t7 = <Button disabled={t6} type="submit">Reset Password</Button>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Link href="/login">Back to Login</Link>;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== handleSubmit || $[21] !== t5 || $[22] !== t7) {
    t9 = <form onSubmit={handleSubmit} className="auth__form" data-testid="forgot-password-form">{t5}{t7}{t8}</form>;
    $[20] = handleSubmit;
    $[21] = t5;
    $[22] = t7;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
};
export const FormFallback = () => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      label: "Email",
      "data-testid": "user-email-error",
      type: "email"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const email = useTextFormGroup(t0);
  let t1;
  if ($[1] !== email) {
    t1 = {
      email
    };
    $[1] = email;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const fields = t1;
  let t2;
  if ($[3] !== fields.email) {
    t2 = <TextFormGroup {...fields.email} />;
    $[3] = fields.email;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Button>Reset Password</Button>;
    t4 = <Link href="/login">Back to Login</Link>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2) {
    t5 = <form className="auth__form" data-testid="forgot-password-form">{t2}{t3}{t4}</form>;
    $[7] = t2;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export default Form;
function _temp(value) {
  if (value.length > 0 && !isValidEmail(value)) {
    return "Email must be a valid email address";
  }
  return null;
}